import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson } from '../lib/api';

// --- Types ---

export type SubscriptionStatus = 'active' | 'pending' | 'cancelled' | 'expired' | 'lapsed';

export interface Subscription {
  id: string;
  policyNumber: string;
  dealId?: string;
  dealName?: string;
  organizationId?: string;
  organizationName?: string;
  contactId?: string;
  contactName?: string;
  productId?: string;
  productName?: string;
  status: SubscriptionStatus;
  premium: number;
  paymentFrequency?: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
  effectiveDate: string;
  expirationDate: string;
  cancelledAt?: string;
  renewedAt?: string;
  paymentStatus?: 'current' | 'overdue' | 'grace_period';
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionFilters {
  status?: SubscriptionStatus;
  organizationId?: string;
  productId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface SubscriptionsResponse {
  subscriptions: Subscription[];
  total: number;
  limit: number;
  offset: number;
}

// --- Helpers ---

function buildQueryString(filters?: SubscriptionFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.organizationId) params.set('organizationId', filters.organizationId);
  if (filters.productId) params.set('productId', filters.productId);
  if (filters.search) params.set('search', filters.search);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Hooks ---

export function useSubscriptions(filters?: SubscriptionFilters) {
  return useQuery({
    queryKey: ['subscriptions', filters],
    queryFn: () => fetchJson<SubscriptionsResponse>(`/api/subscriptions${buildQueryString(filters)}`),
  });
}

export function useSubscription(id: string | null) {
  return useQuery({
    queryKey: ['subscription', id],
    queryFn: () => fetchJson<Subscription>(`/api/subscriptions/${id}`),
    enabled: !!id,
  });
}

export function useCreateSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Subscription>) => postJson<Subscription>('/api/subscriptions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      postJson<Subscription>(`/api/subscriptions/${id}/cancel`, { reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', variables.id] });
    },
  });
}

export function useRenewSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postJson<Subscription>(`/api/subscriptions/${id}/renew`, {}),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', id] });
    },
  });
}
